/**
 * Pure helpers for template builder schema operations.
 * All functions return new objects and never mutate their inputs.
 */

/**
 * Move a section from one index to another.
 * Returns the same schema if the move is a no-op (boundary).
 */
export function moveSection(schema, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0
    || fromIndex >= schema.sections.length || toIndex >= schema.sections.length) {
    return schema;
  }
  const sections = [...schema.sections];
  const [item] = sections.splice(fromIndex, 1);
  sections.splice(toIndex, 0, item);
  return { ...schema, sections };
}

/**
 * Move an option within a choice field from one index to another.
 * Returns the same schema if the move is a no-op.
 */
export function moveOption(schema, sectionId, fieldId, fromIndex, toIndex) {
  return {
    ...schema,
    sections: schema.sections.map(section => {
      if (section.id !== sectionId) return section;
      return {
        ...section,
        fields: section.fields.map(field => {
          if (field.id !== fieldId) return field;
          const options = [...(field.options || [])];
          if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0
            || fromIndex >= options.length || toIndex >= options.length) {
            return field;
          }
          const [item] = options.splice(fromIndex, 1);
          options.splice(toIndex, 0, item);
          return { ...field, options };
        }),
      };
    }),
  };
}

/**
 * Remove an option from a choice field.
 * Clamps max_selections when it would exceed remaining option count.
 */
export function removeOption(schema, sectionId, fieldId, optionId) {
  return {
    ...schema,
    sections: schema.sections.map(section => {
      if (section.id !== sectionId) return section;
      return {
        ...section,
        fields: section.fields.map(field => {
          if (field.id !== fieldId) return field;
          const options = (field.options || []).filter(opt => opt.id !== optionId);
          const validation = { ...(field.validation || {}) };
          if (validation.max_selections != null && validation.max_selections > options.length) {
            validation.max_selections = options.length;
          }
          return { ...field, options, validation };
        }),
      };
    }),
  };
}

/**
 * Map validation errors to field/section controls.
 * Returns Map<string, string[]> where key is fieldId or section path.
 */
export function mapErrorsToFields(errors) {
  const result = new Map();
  for (const err of errors) {
    if (err.path) {
      const existing = result.get(err.path) || [];
      existing.push(err.message);
      result.set(err.path, existing);
    }
    if (err.fieldId) {
      const existing = result.get(err.fieldId) || [];
      existing.push(err.message);
      result.set(err.fieldId, existing);
    }
  }
  return result;
}
